import React from 'react';
import {
  Loading,
  RecordContextProvider,
  useGetOne,
  useRecordContext,
} from 'react-admin';
import { Grid } from '@mui/material';

import auditTrackDiffGenerator, { AuditTrack } from '../audit/auditDiffGenerator/auditTrackDiffGenerator';
import AuditExtraInfo from '../audit/AuditExtraInfo';
import NoResults from '../layout/NoResults';
import TextDiffField from '../utils/auditTracks/TextDiffField';
import DateDiffField from '../utils/auditTracks/DateDiffField';
import StatusDiffField from '../utils/auditTracks/StatusDiffField';
import SelectDiffField from '../utils/auditTracks/SelectDiffField';
import { analysisTypeChoices } from '../../utilities/schemas/analysisTypes';

type IAnalysis = {
  status: string;
  finishedAt: string;
  inputParameters: {
    analysisType: string;
    customPrompt: string;
  };
};

const AnalysisAuditDetails = () => {
  const record = useRecordContext<AuditTrack<IAnalysis>>();
  const { data, isLoading } = useGetOne<AuditTrack<IAnalysis>>(
    `audit-tracks/${record?.id}/previous`,
    { id: '' },
    { enabled: !!record },
  );

  if (!record) return <NoResults />;

  if (!data) {
    if (isLoading) return <Loading />;
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const isElementCreated = data.id === record.id;

  if (!record.documentAfter || (!isElementCreated && !data.documentAfter)) {
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const auditTracksDiff = auditTrackDiffGenerator<IAnalysis>({
    current: record,
    previous: !isElementCreated ? data : undefined,
  });

  const { resourceData } = auditTracksDiff;

  const hasFieldChanges = {
    status: resourceData.status?.hasChanged,
    finishedAt: resourceData.finishedAt?.hasChanged,
    analysisType: resourceData.inputParameters?.analysisType?.hasChanged,
    customPrompt: resourceData.inputParameters?.customPrompt?.hasChanged,
  };
  const hasAnyChanges = Object.values(hasFieldChanges).some((field) => field);

  return (
    <Grid container spacing={6}>
      {!hasAnyChanges && <NoResults text="Only system changes detected" />}
      {hasAnyChanges && (
        <RecordContextProvider value={resourceData}>
          {hasFieldChanges.status && <StatusDiffField source="status" label="Status" />}
          {hasFieldChanges.finishedAt && <DateDiffField source="finishedAt" label="Finished at" showTime />}
          {hasFieldChanges.analysisType && (
            <SelectDiffField
              source="inputParameters.analysisType"
              label="Analysis type"
              choices={analysisTypeChoices}
            />
          )}
          {hasFieldChanges.customPrompt && (
            <TextDiffField source="inputParameters.customPrompt" label="Custom prompt" />
          )}
        </RecordContextProvider>
      )}
      {record.extraInfo && <AuditExtraInfo />}
    </Grid>
  );
};

export default AnalysisAuditDetails;
